import { useState, type CSSProperties, type ReactElement } from 'react';
import { Lien } from '../../app/navigation.js';
import { CIBLE_MIN, COULEURS, OMBRES, styleBouton, styleBoutonPrincipal } from '../../app/styles.js';
import { formaterEuros, type Decision, type DonneesPilotage, type Echeance, type RelanceAValider } from './modele.js';

export interface PropsVueDecisions {
  donnees: DonneesPilotage;
  enCours: readonly string[];
  titresDossiers: Readonly<Record<string, string>>;
  onTrancher: (decisionId: string, option: string) => void;
  onValiderRelance: (messageId: string, decision: 'valider' | 'rejeter') => void;
}

const carte: CSSProperties = {
  border: `1px solid ${COULEURS.bordureDouce}`,
  borderRadius: 12,
  boxShadow: OMBRES.carte,
  padding: 16,
  background: COULEURS.blanc,
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
};
const rangee: CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: 8 };
const surtitre: CSSProperties = {
  margin: 0,
  fontSize: 14,
  fontWeight: 600,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  color: COULEURS.ardoise,
};
const sansMarge: CSSProperties = { margin: 0 };

const lienOuvrir: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  minHeight: CIBLE_MIN,
  color: COULEURS.marine,
  fontWeight: 600,
};
const groupe: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 12 };

const nombreDecisions = (n: number): string => (n === 1 ? '1 décision' : `${n} décisions`);

/** Un groupe par dossier, dans l'ordre d'arrivée des décisions (celui du serveur). */
function grouperParDossier(decisions: readonly Decision[]): { dossierId: string; decisions: Decision[] }[] {
  const groupes = new Map<string, Decision[]>();
  for (const d of decisions) groupes.set(d.dossierId, [...(groupes.get(d.dossierId) ?? []), d]);
  return [...groupes].map(([dossierId, liste]) => ({ dossierId, decisions: liste }));
}

const phraseTitre = (n: number): string =>
  n === 0 ? 'Aucune décision n’attend votre réponse.' : n === 1 ? '1 décision vous attend' : `${n} décisions vous attendent`;

const dateFr = (iso: string): string => iso.slice(0, 10).split('-').reverse().join('/');

function BoutonsDecision({
  decision,
  occupe,
  aConfirmer,
  onChoisir,
  onTrancher,
  onAnnuler,
}: {
  decision: Decision;
  occupe: boolean;
  aConfirmer: string | null;
  onChoisir: (code: string) => void;
  onTrancher: (code: string) => void;
  onAnnuler: () => void;
}): ReactElement {
  const choisie = decision.options.find((o) => o.code === aConfirmer);
  if (choisie) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <p role="status" style={sansMarge}>
          {choisie.costEstimate
            ? `Vous allez choisir « ${choisie.label} » (coût estimé ${formaterEuros(choisie.costEstimate.amountCents)}). Cette décision est enregistrée au journal.`
            : `Vous allez choisir « ${choisie.label} ». Cette décision est enregistrée au journal.`}
        </p>
        <div style={rangee}>
          <button type="button" disabled={occupe} style={styleBoutonPrincipal} onClick={() => onTrancher(choisie.code)}>
            {`Confirmer : ${choisie.label}`}
          </button>
          <button type="button" disabled={occupe} style={styleBouton} onClick={onAnnuler}>
            Annuler
          </button>
        </div>
      </div>
    );
  }
  return (
    <div style={rangee}>
      {decision.options.map((o, i) => (
        <button
          key={o.code}
          type="button"
          disabled={occupe}
          style={{
            ...(i === 0 ? styleBoutonPrincipal : styleBouton),
            display: 'inline-flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={() => onChoisir(o.code)}
        >
          <span>{o.label}</span>
          {o.costEstimate ? <small>{formaterEuros(o.costEstimate.amountCents)}</small> : null}
        </button>
      ))}
    </div>
  );
}

function CarteRelance({
  relance,
  dossier,
  occupe,
  enConfirmation,
  onDemander,
  onAnnuler,
  onValider,
  onRejeter,
}: {
  relance: RelanceAValider;
  dossier: string | undefined;
  occupe: boolean;
  enConfirmation: boolean;
  onDemander: () => void;
  onAnnuler: () => void;
  onValider: () => void;
  onRejeter: () => void;
}): ReactElement {
  return (
    <article data-relance={relance.id} style={carte}>
      <p style={surtitre}>{dossier ? `${dossier} · relance à valider` : 'Relance à valider'}</p>
      <blockquote style={{ margin: 0, paddingLeft: 12, borderLeft: `4px solid ${COULEURS.indigo}`, whiteSpace: 'pre-wrap' }}>
        {relance.renderedBody}
      </blockquote>
      {enConfirmation ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <p role="status" style={sansMarge}>
            Ce message part depuis votre numéro, tel que vous le lisez ci-dessus.
          </p>
          <div style={rangee}>
            <button type="button" disabled={occupe} style={styleBoutonPrincipal} onClick={onValider}>
              Confirmer l’envoi
            </button>
            <button type="button" disabled={occupe} style={styleBouton} onClick={onAnnuler}>
              Annuler
            </button>
          </div>
        </div>
      ) : (
        <div style={rangee}>
          <button type="button" disabled={occupe} style={styleBoutonPrincipal} onClick={onDemander}>
            Valider l’envoi
          </button>
          <button type="button" disabled={occupe} style={styleBouton} onClick={onRejeter}>
            Ne pas envoyer
          </button>
        </div>
      )}
    </article>
  );
}

function Alertes({ echeances, titres }: { echeances: readonly Echeance[]; titres: Readonly<Record<string, string>> }): ReactElement | null {
  const alertes = echeances.filter((e) => e.status === 'proche' || e.status === 'depassee');
  if (alertes.length === 0) return null;
  return (
    <section aria-labelledby="alertes-titre" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <h2 id="alertes-titre" style={{ ...sansMarge, fontSize: 18, color: COULEURS.marine }}>
        Alertes d’échéance
      </h2>
      <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {alertes.map((e) => (
          <li
            key={e.id}
            data-echeance={e.status}
            style={{ ...carte, borderColor: e.status === 'depassee' ? COULEURS.rouge : COULEURS.ambre, background: COULEURS.ambreFond }}
          >
            <strong>{e.label}</strong>
            <span>{`${titres[e.dossierId] ?? 'Dossier'} · ${dateFr(e.dueAt)} · ${e.status === 'depassee' ? 'dépassée' : 'proche'}`}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Décisions en attente, relances à valider et alertes d'échéance (M01 et bloc de D01). Chaque écriture est précédée d'une confirmation. */
export function VueDecisions({ donnees, enCours, titresDossiers, onTrancher, onValiderRelance }: PropsVueDecisions): ReactElement {
  const [aConfirmer, setAConfirmer] = useState<string | null>(null);
  const total = donnees.decisions.length + donnees.relances.length;
  const annuler = (): void => setAConfirmer(null);
  return (
    <div data-vue="decisions" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <h2 style={{ ...sansMarge, fontSize: 20, color: COULEURS.marine }}>{phraseTitre(total)}</h2>
      <div
        data-grille-decisions=""
        style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))', alignItems: 'start' }}
      >
        {grouperParDossier(donnees.decisions).map((g) => (
          <section key={g.dossierId} data-groupe-dossier={g.dossierId} aria-labelledby={`groupe-${g.dossierId}`} style={groupe}>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <div>
                <h3 id={`groupe-${g.dossierId}`} style={{ ...sansMarge, fontSize: 18, color: COULEURS.marine }}>
                  {titresDossiers[g.dossierId] ?? 'Dossier'}
                </h3>
                <p style={{ ...sansMarge, color: COULEURS.ardoise }}>{nombreDecisions(g.decisions.length)}</p>
              </div>
              <Lien vers={`/dossier?dossier=${encodeURIComponent(g.dossierId)}`} style={lienOuvrir} enfants="Ouvrir le dossier" />
            </div>
            {g.decisions.map((d) => (
              <article key={d.id} data-decision={d.id} style={carte}>
                <h4 style={{ ...sansMarge, fontSize: 18 }}>{d.question}</h4>
                <BoutonsDecision
                  decision={d}
                  occupe={enCours.includes(d.id)}
                  aConfirmer={aConfirmer?.startsWith(`d:${d.id}:`) ? aConfirmer.slice(`d:${d.id}:`.length) : null}
                  onChoisir={(code) => setAConfirmer(`d:${d.id}:${code}`)}
                  onTrancher={(code) => {
                    setAConfirmer(null);
                    onTrancher(d.id, code);
                  }}
                  onAnnuler={annuler}
                />
              </article>
            ))}
          </section>
        ))}
      </div>
      {donnees.relances.map((r) => (
        <CarteRelance
          key={r.id}
          relance={r}
          dossier={titresDossiers[r.dossierId]}
          occupe={enCours.includes(r.id)}
          enConfirmation={aConfirmer === `r:${r.id}`}
          onDemander={() => setAConfirmer(`r:${r.id}`)}
          onAnnuler={annuler}
          onValider={() => {
            setAConfirmer(null);
            onValiderRelance(r.id, 'valider');
          }}
          onRejeter={() => onValiderRelance(r.id, 'rejeter')}
        />
      ))}
      {donnees.relancesIndisponible ? (
        <p role="note" style={{ ...sansMarge, color: COULEURS.ambre }}>
          {donnees.relancesIndisponible}
        </p>
      ) : null}
      <Alertes echeances={donnees.echeances} titres={titresDossiers} />
    </div>
  );
}
